/**
 * 保存済みスナップショット (エリア / 路線) の集計を、今の解析結果で選んだ調査項目・細かさに
 * 計算し直す hook (docs/27 §9-3、2026-09-16 オーナー指摘)。
 *
 * 保存済みの `axisCounts` は保存した時点の集計項目で固定されるため、比較ダッシュボードのグラフ・表も
 * 保存時の項目のままだった。この hook は保存された地点 ID を今の解析結果から引き直し、指定の項目・
 * 細かさで集計し直したスナップショット相当を返す (保存済みのデータは書き換えない)。
 *
 * 特定の地区・テンプレートに依存する分岐は持たない (実効の出力フォーマットとマスタだけで動く)。
 */
import { useMemo } from 'react';
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';
import { useDistrict } from '../districts-admin';
import {
  computeAxisCounts,
  computeEvaluationSummary,
  type AxisGranularity,
} from './computeAreaAxisCounts';
import { toPrimaryAxis } from './StreetSurveyAggregationPanel';
import { useStreetSurveyConfig, type ResolvedSurveyAxis } from './useStreetSurveyConfig';
import { useStreetSurveyPosts, type StreetSurveyPost } from './useStreetSurveyPosts';

/** 比較に使うスナップショットの最小形 (エリア / 路線の両方が満たす)。 */
export interface RecomputableSnapshot {
  id: string;
  postIds?: string[];
  pointCount: number;
  axisCounts: { key: string; label: string; color: string; value: number; polarity?: unknown }[];
  evaluation?: unknown;
}

export interface RecomputedSnapshots<T extends RecomputableSnapshot> {
  /** 集計し直したスナップショット (無効時・読込中は元のまま)。 */
  reports: T[];
  /** 選べる調査項目 (実効の出力フォーマット由来)。 */
  axes: ResolvedSurveyAxis[];
  evaluation: EvaluationAxisDef | null;
  /** 既定の集計項目 (地区の設定)。 */
  defaultAxisKey: string | null;
  loading: boolean;
  /** 今の解析結果に見つからなかった地点の数 (合計)。 */
  missingPointCount: number;
}

export function useRecomputedSnapshots<T extends RecomputableSnapshot>(
  districtId: string | null,
  reports: T[],
  options: {
    enabled: boolean;
    axisKey: string | null;
    granularity: AxisGranularity;
    /** 集計する調査 (docs/98 R-97、未指定 = 既定の調査)。 */
    surveyKey?: string;
  },
): RecomputedSnapshots<T> {
  const districtState = useDistrict(districtId);
  const district = districtState.status === 'ready' ? districtState.district : null;
  const postsState = useStreetSurveyPosts(
    options.enabled ? districtId : null,
    district,
    options.surveyKey,
  );
  const posts: StreetSurveyPost[] = useMemo(
    () => (postsState.status === 'ready' ? postsState.posts : []),
    [postsState],
  );
  // 調査項目の一覧は地区の解析設定から解決する (地点を読み込まなくても選択肢を出せる)。
  const settingsId = district?.aiAnalysis?.settingsId ?? posts[0]?.settingsId ?? null;
  const config = useStreetSurveyConfig(district, settingsId, options.surveyKey);

  const byId = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);
  // 未選択なら地区の既定の集計項目を使う。
  const axis =
    config.axes.find((a) => a.key === (options.axisKey ?? config.defaultAggregateAxisKey)) ?? null;

  return useMemo(() => {
    const base = {
      axes: config.axes,
      evaluation: config.evaluation,
      defaultAxisKey: config.defaultAggregateAxisKey,
      loading: options.enabled && (postsState.status === 'loading' || config.loading),
    };
    if (!options.enabled || !axis || posts.length === 0) {
      return { ...base, reports, missingPointCount: 0 };
    }
    let missing = 0;
    const recomputed = reports.map((r) => {
      const ids = r.postIds ?? [];
      const found = ids.map((id) => byId.get(id)).filter((p): p is StreetSurveyPost => !!p);
      missing += ids.length - found.length;
      return {
        ...r,
        pointCount: found.length || r.pointCount,
        axisCounts: computeAxisCounts(
          found,
          toPrimaryAxis(axis),
          config.evaluation,
          options.granularity,
        ),
        evaluation: computeEvaluationSummary(found, config.evaluation),
      };
    });
    return { ...base, reports: recomputed, missingPointCount: missing };
  }, [
    reports,
    byId,
    posts.length,
    axis,
    config.axes,
    config.evaluation,
    config.defaultAggregateAxisKey,
    config.loading,
    options.enabled,
    options.granularity,
    postsState.status,
  ]);
}
